import React, { useState } from "react";
import { useAuth } from "./AuthContext";
import { FiLogOut } from "react-icons/fi";
import { Tooltip } from "@mui/material";

const overlayStyle = {
  position: "fixed",
  inset: 0,
  zIndex: 1300,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "rgba(9, 30, 30, 0.72)",
  backdropFilter: "blur(6px)",
  WebkitBackdropFilter: "blur(6px)",
  animation: "lgFadeIn 0.2s ease both",
};

const cardStyle = {
  position: "relative",
  width: "100%",
  maxWidth: 380,
  margin: "0 16px",
  padding: "32px 32px 28px",
  borderRadius: 18,
  background: "rgba(20, 50, 50, 0.82)",
  backdropFilter: "blur(18px)",
  WebkitBackdropFilter: "blur(18px)",
  border: "1px solid rgba(255,255,255,0.08)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.55), 0 1px 0 rgba(255,255,255,0.06) inset",
  fontFamily: "'Inter', 'Segoe UI', sans-serif",
  animation: "lgSlideUp 0.25s ease both",
};

const iconWrapStyle = {
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  width: 50,
  height: 50,
  borderRadius: 14,
  background: "linear-gradient(135deg, #2dd4bf, #14b8a6)",
  boxShadow: "0 6px 20px rgba(20,184,166,0.45)",
  marginBottom: 16,
};

const titleStyle = {
  fontSize: 18,
  fontWeight: 700,
  color: "#e2f0ef",
  letterSpacing: "0.5px",
  marginBottom: 8,
};

const bodyStyle = {
  fontSize: 13,
  color: "#7fa8a8",
  marginBottom: 28,
  lineHeight: 1.6,
};

const footerStyle = {
  display: "flex",
  justifyContent: "flex-end",
  gap: 10,
};

const cancelBtnStyle = {
  padding: "9px 22px",
  borderRadius: 50,
  border: "1px solid rgba(255,255,255,0.15)",
  background: "transparent",
  color: "#a0c4c4",
  fontSize: 13,
  fontWeight: 600,
  cursor: "pointer",
  fontFamily: "inherit",
  transition: "all 0.2s",
};

const logoutBtnStyle = {
  padding: "9px 22px",
  borderRadius: 50,
  border: "none",
  background: "linear-gradient(90deg, #2dd4bf, #14b8a6)",
  color: "#fff",
  fontSize: 13,
  fontWeight: 700,
  letterSpacing: "0.8px",
  cursor: "pointer",
  fontFamily: "inherit",
  boxShadow: "0 4px 14px rgba(20,184,166,0.4)",
  transition: "all 0.2s",
};

const closeBtnStyle = {
  position: "absolute",
  top: 14,
  right: 16,
  background: "transparent",
  border: "none",
  color: "#4d7a7a",
  fontSize: 20,
  cursor: "pointer",
  lineHeight: 1,
  padding: 0,
  transition: "color 0.2s",
};

const triggerBtnStyle = {
  background: "transparent",
  border: "none",
  cursor: "pointer",
  padding: "4px 6px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const keyframes = `
  @keyframes lgFadeIn {
    from { opacity: 0; }
    to   { opacity: 1; }
  }
  @keyframes lgSlideUp {
    from { opacity: 0; transform: translateY(20px); }
    to   { opacity: 1; transform: translateY(0); }
  }
`;

const Logout = () => {
  const { logout } = useAuth();
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [cancelHover, setCancelHover] = useState(false);
  const [logoutHover, setLogoutHover] = useState(false);
  const [closeHover, setCloseHover] = useState(false);

  const handleLogout = () => setShowConfirmation(true);
  const handleConfirmLogout = () => {
    logout();
    setShowConfirmation(false);
  };
  const handleCancelLogout = () => setShowConfirmation(false);

  return (
    <>
      <style>{keyframes}</style>

      {/* Trigger button */}
      <Tooltip
        title="Logout"
        arrow
        componentsProps={{
          tooltip: {
            sx: {
              backgroundColor: "#14b8a6",
              color: "#fff",
              fontWeight: 600,
              fontSize: "11px",
              boxShadow: 2,
            },
          },
          arrow: { sx: { color: "#14b8a6" } },
        }}
      >
        <button onClick={handleLogout} style={triggerBtnStyle} aria-label="Logout">
          <FiLogOut style={{ color: "whitesmoke", fontSize: 18 }} />
        </button>
      </Tooltip>

      {/* Confirm modal */}
      {showConfirmation && (
        <div style={overlayStyle} onClick={handleCancelLogout}>
          <div style={cardStyle} onClick={(e) => e.stopPropagation()}>

            {/* Close × */}
            <button
              style={{ ...closeBtnStyle, color: closeHover ? "#2dd4bf" : "#4d7a7a" }}
              onClick={handleCancelLogout}
              onMouseEnter={() => setCloseHover(true)}
              onMouseLeave={() => setCloseHover(false)}
              aria-label="Close"
            >
              ×
            </button>

            {/* Icon */}
            <div style={iconWrapStyle}>
              <FiLogOut style={{ color: "#fff", fontSize: 22 }} />
            </div>

            {/* Title */}
            <div style={titleStyle}>Confirm Logout</div>

            {/* Body */}
            <div style={bodyStyle}>
              Are you sure you want to logout from your account?
            </div>

            {/* Footer */}
            <div style={footerStyle}>
              <button
                style={{
                  ...cancelBtnStyle,
                  background: cancelHover ? "rgba(255,255,255,0.07)" : "transparent",
                  borderColor: cancelHover ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.15)",
                  color: cancelHover ? "#d4eded" : "#a0c4c4",
                }}
                onClick={handleCancelLogout}
                onMouseEnter={() => setCancelHover(true)}
                onMouseLeave={() => setCancelHover(false)}
              >
                Cancel
              </button>
              <button
                style={{
                  ...logoutBtnStyle,
                  opacity: logoutHover ? 0.9 : 1,
                  transform: logoutHover ? "translateY(-1px)" : "none",
                  boxShadow: logoutHover
                    ? "0 8px 22px rgba(20,184,166,0.55)"
                    : "0 4px 14px rgba(20,184,166,0.4)",
                }}
                onClick={handleConfirmLogout}
                onMouseEnter={() => setLogoutHover(true)}
                onMouseLeave={() => setLogoutHover(false)}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Logout;
